import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import Quill from 'quill';
import Delta from 'quill-delta';
import { Range } from 'quill/core/selection';

import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { TextVariableService } from '@sb-tools/typescript/services/text-variable.service';
import { QuillQuickAccessService } from '@sb-tools/typescript/services/quill/quill-quick-access.service';
import { CategoryWithReplyTemplate } from '@sb-tools/typescript/models/category-with-reply-template.model';
import { InboxAttachmentBase } from '@sb-tools/typescript/models/inbox-attachment-base.model';
import { ReplyTemplate } from '@sb-tools/typescript/models/reply-template.model';
import { QuillQuickAccessOptions } from '@sb-tools/typescript/models/quill-quick-access-options.model';
import { ReplyUpdateModel } from '@sb-tools/typescript/models/reply-update-model.model';
import { NoteGuiModel } from '@sb-tools/typescript/models/view/note-gui-model.model';
import { ThreadElementDTO } from '@sb-tools/typescript/models/dto/thread-element.dto';
import { NewLinePipe } from '@sb-tools/typescript/pipes/new-line.pipe';

import { ReplyTemplateService } from '../core/reply-template.service';
import { TemplateEntity } from '../core/template-entity.model';

import { ReplyTemplatesModalComponent } from 'src/sb-modules/Frontend-Reply-Templates-Module/reply-templates-modal.component';
import { FormCommonDataService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-common-data.service';
import { ClickOutsideDirective } from '@sb-tools/typescript/directives/click-outside.directive';
import { InlineSVGComponent } from '../../Frontend-Inline-SVG-Module/inline-svg.component';
import { NgClass } from '@angular/common';

type TemplateType = Partial<CategoryWithReplyTemplate> | Partial<ReplyTemplate>;

@Component({
    selector: 'sb-quill-reply-templates',
    templateUrl: './quill-reply-templates.component.html',
    styleUrls: ['./quill-reply-templates.component.scss'],
    imports: [ClickOutsideDirective, InlineSVGComponent, NgClass],
})
export class QuillReplyTemplatesComponent implements AfterViewInit, OnInit {
    @ViewChild('templatesContainer') templatesContainerElement: ElementRef;
    @ViewChild('templatesList') templatesListElement: ElementRef;

    @Input() attachments?: InboxAttachmentBase[];
    @Input() emitAttachments?: boolean;
    @Input() isTextContent?: boolean;
    @Input() note?: NoteGuiModel;
    @Input() parentContainerId?: string;
    @Input() quillEditor: Quill;
    @Input() reply?:
        | ReplyUpdateModel
        | ThreadElementDTO
        | {
              text: string;
              html: string;
              attachments: any[];
          };
    @Input() summernote: JQuery<HTMLElement>;

    filteredTemplates: TemplateType[] = [];
    replyTemplates: TemplateType[] = [];

    private cursorPos: number;
    private isOpen: boolean;
    private itemIndex: number;
    private options: QuillQuickAccessOptions = {
        dropdownItemHeight: 28,
        dropdownListMaxHeight: 200,
        denotationChar: '/',
        showDenotationChar: false,
        allowedChars: /^[/A-Za-z\sÆØÅæøåÄÖäö0-9!#$%&+_~\-.]*$/,
        minChars: 0,
        maxChars: 31,
        offsetTop: 5,
        offsetLeft: 0,
        isFixedPosition: true,
        isolateCharacter: true,
        fixContainerToQuill: false,
        defaultMenuOrientation: 'top',
        blotName: 'templates',
        dataAttributes: [],
        linkTarget: '',

        // Style options
        listItemClass: 'ql-templates-list-item',
        dropdownContainerClass: 'ql-templates-list-container',
        itemsListClass: 'ql-templates-list',
        spaceAfterInsert: true,
        parentContainerId: null,
    };
    private replacingValue: string;
    private suspendMouseEnter;
    private templateCharPos;
    private templatesContainer: HTMLElement;
    private templatesList: HTMLElement;

    constructor(
        private newLinePipe: NewLinePipe,
        private bsModalService: BsModalService,
        private textVariableService: TextVariableService,
        private replyTemplateService: ReplyTemplateService,
        private translateService: TranslateService,
        private quillQuickAccessService: QuillQuickAccessService,
        private changeDetectorRef: ChangeDetectorRef,
        private formCommonDataService: FormCommonDataService,
    ) {}

    ngOnInit(): void {
        this.initTemplatesList();

        this.options.parentContainerId = this.parentContainerId;
        if (this.quillEditor) {
            const keyboardBindings = {
                textChangeHandler: this.onTextChange.bind(this),
                selectionChangeHandler: this.onSelectionChange.bind(this),
                enterHandler: this.selectHandler.bind(this),
                escapeHandler: this.hideTemplatesList.bind(this),
                upHandler: this.upHandler.bind(this),
                downHandler: this.downHandler.bind(this),
            };
            this.quillQuickAccessService.addQuickAccessBindings(this.quillEditor, keyboardBindings);
        } else if (this.summernote) {
            this.summernote['summernote']().on('summernote.keydown', (we: Event, event: KeyboardEvent) => {
                this.summernoteHandler(event);
            });
        }
    }

    ngAfterViewInit(): void {
        this.templatesContainer = this.templatesContainerElement.nativeElement;
        this.templatesList = this.templatesListElement.nativeElement;

        this.quillQuickAccessService.initDropdownStyles(this.templatesContainer, this.options);

        this.templatesContainer.onmousemove = this.onContainerMouseMove.bind(this);
    }

    chooseTemplate(event: Event, template: TemplateType, index: number): void {
        event.preventDefault();
        event.stopImmediatePropagation();

        this.itemIndex = index;
        this.highlightItem();
        this.selectItem();
    }

    hideTemplatesList(): void {
        this.quillQuickAccessService.hideDropdown(this.templatesContainer);
        this.isOpen = false;
    }

    onItemMouseEnter(index: number): void {
        if (this.suspendMouseEnter) {
            return;
        }

        if (index !== this.itemIndex) {
            this.itemIndex = index;
            this.highlightItem(false);
        }
    }

    private downHandler(): boolean {
        if (this.isOpen) {
            this.nextItem();
            return false;
        }
        return true;
    }

    private summernoteHandler(event: KeyboardEvent): void {
        switch (event.key) {
            case 'Enter': {
                if (!this.isOpen) {
                    this.summernote['summernote']('insertText', '');
                } else {
                    event.preventDefault();
                }
                this.selectHandler();
                break;
            }
            case 'Escape': {
                if (this.isOpen) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                this.hideTemplatesList();
                break;
            }
            case 'ArrowUp': {
                if (this.isOpen) {
                    event.preventDefault();
                }
                this.upHandler();
                break;
            }
            case 'ArrowDown': {
                if (this.isOpen) {
                    event.preventDefault();
                }
                this.downHandler();
                break;
            }
            default: {
                setTimeout(() => {
                    this.onSummernoteSomethingChange();
                }, 300);
                break;
            }
        }
    }

    private getFilteredTemplates(searchTerm: string): TemplateType[] {
        const processedTemplates = this.replyTemplates;

        if (searchTerm.trimStart().length === 0) {
            return processedTemplates;
        }

        const matches = [];
        searchTerm = searchTerm.trimStart().toLowerCase();

        const searchInChildrenNodes = (template, keyword, result) => {
            if (template.isGroupName || template.name.toLowerCase().indexOf(keyword) >= 0) {
                result.push(template);
            }

            if (template.replyTemplates?.length) {
                template.replyTemplates.forEach((item) => {
                    if (item.name.toLowerCase().indexOf(keyword) >= 0) {
                        item.parentData = [...template.parentData];
                        item.parentData.push({ id: template.id, name: template.name, label: template.label });
                        result.push(item);
                    }
                });
            }

            if (template.children?.length) {
                for (const child of (template as CategoryWithReplyTemplate).children) {
                    searchInChildrenNodes(child, keyword, result);
                }
            }
        };

        for (const template of processedTemplates) {
            searchInChildrenNodes(template, searchTerm, matches);
        }

        const isNoPersonalTemplates = matches.length > 1 && matches[0].isGroupName && matches[1].isGroupName;

        // remove title 'Personal templates' form templates list
        if (isNoPersonalTemplates) {
            matches.shift();
        }

        const isNoCommunityTemplates = matches.length && matches[matches.length - 1].isGroupName;

        // remove title 'Community templates' form templates list
        if (isNoCommunityTemplates) {
            matches.pop();
        }

        return matches;
    }

    private getTemplateCharIndex(text: string, templateChar: string | string[], isIsolateChar: boolean): number {
        let templateCharIndex = -1;

        if (isIsolateChar) {
            for (let i = text?.length - 1; i >= 0; i--) {
                if (text[i] === templateChar && this.isCharIsolated(text, i)) {
                    templateCharIndex = i;
                    break;
                }
            }
        } else {
            templateCharIndex = text.lastIndexOf(templateChar as string);
        }

        return templateCharIndex;
    }

    private hasValidChars(text: string, allowedChars: RegExp): boolean {
        return allowedChars.test(text);
    }

    private hasValidTemplateCharIndex(templateCharIndex: number): boolean {
        return templateCharIndex > -1;
    }

    private highlightFirstDropdownItem(): void {
        if (this.filteredTemplates.length) {
            this.itemIndex = 0;
            this.highlightItem(false);
        }
    }

    private highlightItem(scrollItemInView = true): void {
        if (!this.isOpen) {
            return;
        }
        this.filteredTemplates.forEach((template) => {
            template.isSelected = false;
        });

        if (this.filteredTemplates[this.itemIndex].isGroupName) {
            this.itemIndex += 1;
        }

        this.filteredTemplates[this.itemIndex].isSelected = true;

        if (scrollItemInView) {
            this.quillQuickAccessService.scrollActiveDropdownItemToView(
                this.templatesContainer,
                this.templatesList,
                this.itemIndex,
            );
        }

        this.changeDetectorRef.detectChanges();
    }

    private initReplyTemplatesList(categoryWithReplyTemplate: TemplateType): void {
        if (this.isCategoryWithReplyTemplates(categoryWithReplyTemplate)) {
            this.filteredTemplates = [
                ...(categoryWithReplyTemplate as CategoryWithReplyTemplate).children,
                ...(categoryWithReplyTemplate as CategoryWithReplyTemplate).replyTemplates,
            ];
        } else {
            this.filteredTemplates = [categoryWithReplyTemplate];
        }

        this.highlightFirstDropdownItem();
    }

    private initTemplatesList(): void {
        const personalReplyTemplatesWithTitleElement = [
            ...(this.replyTemplateService.personalReplyTemplates.length
                ? [{ isGroupName: true, name: this.translateService.instant('Personal templates'), isSelected: false }]
                : []),
            ...this.replyTemplateService.personalReplyTemplates,
        ];

        const communityReplyTemplateWithTitleElements = [
            ...(this.replyTemplateService.replyTemplates.length
                ? [{ isGroupName: true, name: this.translateService.instant('Community templates'), isSelected: false }]
                : []),
            ...this.replyTemplateService.replyTemplates,
        ];

        this.replyTemplates = [...personalReplyTemplatesWithTitleElement, ...communityReplyTemplateWithTitleElements];
    }

    private insertItem(templateText: string, attachments: InboxAttachmentBase[]): void {
        if (this.quillEditor) {
            const prevTemplateCharPos = this.templateCharPos;

            // delete template char '/'
            this.quillEditor.deleteText(
                this.templateCharPos,
                this.cursorPos - this.templateCharPos,
                Quill.sources.USER,
            );

            this.quillEditor.clipboard.dangerouslyPasteHTML(
                prevTemplateCharPos,
                this.newLinePipe.transform(templateText),
                'user',
            );
        } else if (this.summernote) {
            const lastRange = this.summernote['summernote']('editor.getLastRange');
            let editElement = lastRange.ec;
            const elementNodeType = 1;
            if (editElement.nodeType !== elementNodeType) {
                editElement = this.summernote['summernote']('editor.getLastRange').ec.parentNode;
            }

            editElement.innerHTML = editElement.innerHTML.replace(this.replacingValue, templateText);
            const range = jQuery['summernote']['range']; // range utility
            this.summernote['summernote']('editor.setLastRange', range.createFromNodeAfter(editElement).select()); // set my custom range
            this.summernote['summernote']('insertText', ''); // save changing in local storage
        }

        if (attachments.length) {
            if (this.attachments) {
                this.attachments.push(...attachments);
            } else {
                const entity = this.reply || this.note;
                if (entity) {
                    entity.attachments = [...entity.attachments, ...attachments];
                }
            }
            if (this.emitAttachments) {
                this.formCommonDataService.attachmentsAddedSubject.next(attachments);
            }
        }

        this.hideTemplatesList();
    }

    private isCategoryWithReplyTemplates(template: TemplateType): boolean {
        return !!(
            (template as CategoryWithReplyTemplate).children?.length ||
            (template as CategoryWithReplyTemplate).replyTemplates?.length
        );
    }

    private isCharIsolated(text: string, charIndex: number): boolean {
        return charIndex === 0 || !!text[charIndex - 1].match(/\s/g);
    }

    private nextItem(): void {
        this.itemIndex = (this.itemIndex + 1) % this.filteredTemplates.length;
        this.suspendMouseEnter = true;
        this.highlightItem();
    }

    private onContainerMouseMove(): void {
        this.suspendMouseEnter = false;
    }

    private onSelectionChange(range: Range): void {
        if (range && range.length === 0) {
            this.onSomethingChange();
        } else {
            this.hideTemplatesList();
        }
    }

    private onSomethingChange(): void {
        const range = this.quillEditor.getSelection();

        if (range == null) {
            return;
        }

        this.cursorPos = range.index;
        const textBeforeCursor = this.quillQuickAccessService.getTextBeforeCursor(
            this.quillEditor,
            this.cursorPos,
            this.options.maxChars,
        );

        const templateChar = this.options.denotationChar;

        const templateCharIndex = this.getTemplateCharIndex(
            textBeforeCursor,
            templateChar,
            this.options.isolateCharacter,
        );

        if (this.hasValidTemplateCharIndex(templateCharIndex)) {
            const templateCharPos = this.cursorPos - (textBeforeCursor.length - templateCharIndex);
            this.templateCharPos = templateCharPos;
            const textAfter = textBeforeCursor.substring(templateCharIndex + templateChar.length);

            if (textAfter.length >= this.options.minChars && this.hasValidChars(textAfter, this.options.allowedChars)) {
                this.renderList(textAfter);
            } else {
                this.hideTemplatesList();
            }
        } else {
            this.hideTemplatesList();
        }
    }

    private onSummernoteSomethingChange(): void {
        const range = this.summernote['summernote']('editor.getLastRange');

        if (range == null) {
            return;
        }

        this.cursorPos = range.so;
        const textBeforeCursor = range.sc.data?.substring(0, this.cursorPos);

        const templateChar = this.options.denotationChar;

        const templateCharIndex = this.getTemplateCharIndex(
            textBeforeCursor,
            templateChar,
            this.options.isolateCharacter,
        );

        if (this.hasValidTemplateCharIndex(templateCharIndex)) {
            const templateCharPos = this.cursorPos - (textBeforeCursor.length - templateCharIndex);

            this.templateCharPos = templateCharPos;
            const textAfter = textBeforeCursor.substring(templateCharIndex + templateChar.length);
            this.replacingValue = textBeforeCursor.substring(templateCharIndex + templateChar.length - 1);

            if (textAfter.length >= this.options.minChars && this.hasValidChars(textAfter, this.options.allowedChars)) {
                this.renderList(textAfter);
            } else {
                this.hideTemplatesList();
            }
        } else {
            this.hideTemplatesList();
        }
    }

    private onTextChange(delta: Delta, oldContents: Delta, source: 'api' | 'silent' | 'user'): void {
        if (source === 'user') {
            // setTimeout need for fixing problem with updating search results when pasting text in templates
            setTimeout(() => {
                this.onSomethingChange();
            }, 1);
        }
    }

    private prevItem(): void {
        this.itemIndex = (this.itemIndex + this.filteredTemplates.length - 1) % this.filteredTemplates.length;

        if (this.filteredTemplates[this.itemIndex].isGroupName) {
            this.itemIndex = !this.itemIndex ? this.filteredTemplates.length - 1 : (this.itemIndex -= 1);
        }

        this.suspendMouseEnter = true;
        this.highlightItem();
    }

    private renderList(searchTerm: string): void {
        const filteredTemplates = this.getFilteredTemplates(searchTerm);

        if (filteredTemplates && filteredTemplates.length > 0) {
            this.filteredTemplates = filteredTemplates;
            this.itemIndex = 0;
            this.highlightItem();

            setTimeout(() => {
                this.showTemplatesList(this.filteredTemplates.length);
            }, 100);
        } else {
            this.hideTemplatesList();
        }
    }

    private selectHandler(): boolean {
        if (this.isOpen) {
            this.selectItem();
            return false;
        }
        return true;
    }

    private selectItem(): void {
        const template = this.filteredTemplates[this.itemIndex];

        if (this.isCategoryWithReplyTemplates(template)) {
            this.initReplyTemplatesList(template as CategoryWithReplyTemplate);
            return;
        }

        const selectedTemplateText = this.isTextContent
            ? HelperService.strip((template as ReplyTemplate).text, false)
            : (template as ReplyTemplate).text;

        const variables = this.textVariableService.getVariables(selectedTemplateText);

        if (variables.length > 0) {
            const initialState = {
                isVariablesView: true,
                variables,
                selectedTemplate: TemplateEntity.castFromReplyTemplate(template as ReplyTemplate),
                textWithVariables: selectedTemplateText,
                replyTemplate: template as ReplyTemplate,
                isTextContent: this.isTextContent,
                hasBackButton: false,
                confirmCallback: (templateText, attachments) => {
                    this.insertItem(templateText, attachments);
                },
            };

            this.bsModalService.show(ReplyTemplatesModalComponent, {
                initialState,
            });
        } else {
            this.insertItem(selectedTemplateText, structuredClone((template as ReplyTemplate).attachments));
        }

        this.hideTemplatesList();
    }

    private showTemplatesList(templatesLength: number): void {
        if (this.quillEditor) {
            this.quillQuickAccessService.showDropdown(
                this.quillEditor,
                this.options,
                templatesLength,
                this.templatesContainer,
                this.templateCharPos,
            );
        } else if (this.summernote) {
            this.quillQuickAccessService.showDropdown(
                null,
                this.options,
                templatesLength,
                this.templatesContainer,
                this.templateCharPos,
            );
        }

        this.isOpen = true;

        this.highlightFirstDropdownItem();
    }

    private upHandler(): boolean {
        if (this.isOpen) {
            this.prevItem();
            return false;
        }
        return true;
    }
}
